/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * timeTrackingApi.js
 *
 * The in-app Time Tracker's data layer — the mobile twin of the admin panel's
 * `GlobalTimeTrackerWidget`. It answers four questions the tracker screen asks:
 * what am I tracking right now, what could I track, which project/task is this,
 * and how much have I logged today.
 *
 * WHY THE STATE COMES FROM `/widgets/summary` AND NOT `/projects/me/running-timer`:
 * the app tracks TWO worlds — project timers (`project_time_entries`) and service
 * work-order segments — and only the widget aggregator resolves both into one
 * `timer` object carrying the authoritative `state` (`running|break|paused`), the
 * capability flags and the paused row's own settled duration. The project-only
 * endpoint would report "nothing is running" for a technician who is mid-job, and
 * the screen would then happily start a second clock. `/widgets/summary` is also
 * exactly what the home-screen widget reads, so the screen and the widget can
 * never disagree about what is running.
 *
 * WHY THE TIMER VERBS ARE REPEATED HERE INSTEAD OF REUSING `widgets/widgetActions`:
 * that module is the headless widget's action layer and is contractually
 * never-throwing — every failure collapses to `{ok: false, timer: null}`, which is
 * right for a home-screen button that can only repaint, and wrong for a screen
 * that must tell the user WHY (a 403 `service_feature_disabled` is a different
 * sentence from a dropped connection). {@link callTimerFacade} therefore hits the
 * same `/widgets/timer/*` endpoints and lets the error through with its `code`
 * intact. Both layers stay on the one facade, so there is still a single backend
 * contract — see `app/helpers/widgets/widgetActions.js` for the widget side.
 *
 * Reads are FAIL-SOFT (a transport error degrades to an empty page / null state,
 * mirroring `helpers/salesApi`), writes THROW so the screen can toast the reason.
 *
 * @module helpers/timeTrackingApi
 */

import {apiFetch} from './WorkSheetContext';

/** Page size for the project / task pickers. Matches the backend's own default. */
export const TIME_TRACKING_PAGE_SIZE = 20;

/** Hard ceiling the backend applies to `limit` on `/projects` and `/tasks`. */
const PAGE_SIZE_MAX = 200;

/** Base path of the unified widget timer facade (backend `widgets.controller`). */
const TIMER_BASE = '/widgets/timer';

/**
 * @typedef {object} TrackerPage
 * @property {boolean}  ok     False when the read FAILED (vs. genuinely empty).
 * @property {object[]} items  Rows of this page.
 * @property {number}   total  Total matching records reported by the server.
 * @property {number}   page   Page number actually returned.
 * @property {number}   limit  Page size actually returned.
 * @property {number}   pages  Total page count.
 */

/**
 * @typedef {object} TrackerState
 * @property {object|null}  timer      Unified timer (contract C1) or null when idle.
 * @property {object[]}     trackables Quick-start candidates (contract C2).
 * @property {boolean}      ok         False when the read failed (vs. genuinely idle).
 */

const logTimeTrackingError = (operation, error) => {
    console.warn(`[timeTrackingApi] ${operation} failed:`, error?.message || error);
};

/**
 * Scope header for the store the user is actually looking at. Project and task
 * reads resolve their tenant from `x-restaurant-id` and otherwise fall back to
 * the caller's PRIMARY restaurant, so a multi-store manager who picked store B
 * would otherwise browse store A's projects.
 * @param {{restaurantId?: string}} [scope] Active management scope.
 * @returns {Record<string, string>} Headers to merge into an `apiFetch` call.
 */
const scopeHeaders = (scope) => (scope?.restaurantId ? {'x-restaurant-id': String(scope.restaurantId)} : {});

/**
 * Build a query string (no leading `?`), dropping empty values but keeping `0`
 * and `false`.
 * @param {Record<string, *>} [params] Query parameters.
 * @returns {string} Encoded query string.
 */
const buildQuery = (params = {}) => Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
    .join('&');

const clampLimit = (limit) => {
    const numeric = Number(limit);

    if (!Number.isFinite(numeric) || numeric < 1) {
        return TIME_TRACKING_PAGE_SIZE;
    }

    return Math.min(PAGE_SIZE_MAX, Math.floor(numeric));
};

const clampPage = (page) => {
    const numeric = Number(page);

    if (!Number.isFinite(numeric) || numeric < 1) {
        return 1;
    }

    return Math.floor(numeric);
};

const firstNumber = (candidates, fallback) => {
    for (const candidate of candidates) {
        const numeric = Number(candidate);
        if (Number.isFinite(numeric)) {
            return numeric;
        }
    }

    return fallback;
};

/**
 * Pull the row array out of any envelope the backend answers with. `/projects`
 * and `/tasks` both wrap it as `{success, data: {projects|tasks, pagination}}`,
 * but the bare-array and `{docs}` shapes are tolerated too so a proxy or an older
 * deployment degrades instead of throwing.
 * @param {*} response Parsed response body.
 * @param {string[]} [keys] Envelope keys holding the rows.
 * @returns {object[]} The rows, `[]` on an unknown shape.
 */
const normalizeList = (response, keys = []) => {
    if (Array.isArray(response)) {
        return response;
    }

    for (const key of keys) {
        if (Array.isArray(response?.[key])) {
            return response[key];
        }
        if (Array.isArray(response?.data?.[key])) {
            return response.data[key];
        }
    }

    if (Array.isArray(response?.data)) {
        return response.data;
    }
    if (Array.isArray(response?.docs)) {
        return response.docs;
    }

    return [];
};

/**
 * Fold a paginated envelope into a {@link TrackerPage}. The backend nests its
 * metadata as `data.pagination`, so a top-level-only read would report one page
 * of everything and the picker would never offer "load more".
 * @param {*} response Parsed response body.
 * @param {string[]} [keys] Envelope keys holding the rows.
 * @param {{page?: number, limit?: number}} [requested] What the caller asked for.
 * @returns {TrackerPage} The normalized page.
 */
const normalizePage = (response, keys = [], requested = {}) => {
    const items = normalizeList(response, keys);
    const meta = (response && !Array.isArray(response) && typeof response === 'object')
        ? (response.pagination || response.data?.pagination || {})
        : {};

    const limit = clampLimit(firstNumber([meta.limit], clampLimit(requested.limit)));
    const page = clampPage(firstNumber([meta.page], clampPage(requested.page)));
    const total = firstNumber([meta.total], items.length);
    const pages = firstNumber([meta.pages], limit > 0 ? Math.max(1, Math.ceil(total / limit)) : 1);

    return {ok: true, items, total, page, limit, pages: Math.max(1, pages)};
};

/**
 * An empty page.
 *
 * `ok` is the whole point: the readers below are fail-soft, so without it a
 * transport failure and a genuinely empty result are the same value — and the
 * picker would answer a 500 with "no such project, type its name and we will
 * create it", inviting a duplicate of a project that exists and is merely
 * unreachable. Callers turn `ok: false` into a retry affordance.
 *
 * @param {number} [page] Requested page.
 * @param {number} [limit] Requested page size.
 * @param {boolean} [ok] False for the fail-soft answer of a failed read.
 * @returns {TrackerPage}
 */
export const emptyTrackerPage = (page = 1, limit = TIME_TRACKING_PAGE_SIZE, ok = true) => ({
    ok,
    items: [],
    total: 0,
    page: clampPage(page),
    limit: clampLimit(limit),
    pages: 1,
});

/**
 * Unwrap the `{success, data}` envelope the project/task controllers answer a
 * single record with. Tolerates a bare document so a proxy that already unwrapped
 * it still works.
 * @param {*} response Parsed response body.
 * @returns {object|null} The record, or null when none could be found.
 */
const normalizeRecord = (response) => {
    if (!response || typeof response !== 'object') {
        return null;
    }
    const record = response.data && typeof response.data === 'object' ? response.data : response;

    return record && (record._id || record.id) ? record : null;
};

/**
 * Read the caller's unified timer state plus the quick-start candidates.
 *
 * Fail-soft, but distinguishably so: `ok: false` means the read itself failed,
 * which is NOT the same as "nothing is running" — the screen must not offer
 * Start on a failed read, or a technician with a live clock would open a second.
 *
 * @param {{restaurantId?: string}} [scope] Active management scope.
 * @returns {Promise<TrackerState>} Never throws.
 */
export const fetchTrackerState = async (scope) => {
    try {
        // `/widgets/summary` resolves its store from `x-restaurant-id` and
        // otherwise falls back to the caller's PRIMARY restaurant
        // (`utils/restaurantScope.util#resolveActiveRestaurantScopeId`), so a
        // multi-store user who picked store B would see store A's quick-start
        // list. `skipDedup` is mandatory alongside it: `apiFetch` keys its
        // in-flight GET dedupe on the PATH ALONE, and the widget snapshot
        // builder calls this same path UNSCOPED — without it the two requests
        // collapse into whichever fired first and one of them silently gets the
        // wrong store.
        const response = await apiFetch('/widgets/summary', {
            headers: scopeHeaders(scope),
            skipDedup: true,
        });
        const timer = response?.timer && response.timer.id ? response.timer : null;
        const trackables = (Array.isArray(response?.trackables) ? response.trackables : [])
            .filter((item) => item && item.id);

        return {ok: true, timer, trackables};
    } catch (error) {
        logTimeTrackingError('fetchTrackerState', error);

        return {ok: false, timer: null, trackables: []};
    }
};

/**
 * Search the projects a timer can be started on.
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {{search?: string, page?: number, limit?: number}} [options] Query options.
 * @returns {Promise<TrackerPage>} Fail-soft — an empty page on any error.
 */
export const fetchTrackerProjects = async (scope, options = {}) => {
    const page = clampPage(options.page);
    const limit = clampLimit(options.limit);

    try {
        const query = buildQuery({
            page,
            limit,
            search: options.search,
            sortBy: 'updatedAt',
            sortOrder: 'desc',
        });
        const response = await apiFetch(`/projects?${query}`, {headers: scopeHeaders(scope)});

        return normalizePage(response, ['projects'], {page, limit});
    } catch (error) {
        logTimeTrackingError('fetchTrackerProjects', error);

        return emptyTrackerPage(page, limit, false);
    }
};

/**
 * Search the tasks of one project.
 *
 * Deliberately `/tasks?projectId=` rather than `/tasks/autocomplete`: the
 * autocomplete endpoint searches across EVERY project the caller can see, so the
 * picker would offer tasks that do not belong to the project the timer is about
 * to be booked against.
 *
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {{projectId: string, search?: string, page?: number, limit?: number}} [options] Query options.
 * @returns {Promise<TrackerPage>} Fail-soft; an empty page when `projectId` is missing.
 */
export const fetchTrackerTasks = async (scope, options = {}) => {
    const page = clampPage(options.page);
    const limit = clampLimit(options.limit);

    if (!options.projectId) {
        return emptyTrackerPage(page, limit);
    }

    try {
        const query = buildQuery({
            projectId: options.projectId,
            q: options.search,
            page,
            limit,
        });
        const response = await apiFetch(`/tasks?${query}`, {headers: scopeHeaders(scope)});

        return normalizePage(response, ['tasks'], {page, limit});
    } catch (error) {
        logTimeTrackingError('fetchTrackerTasks', error);

        return emptyTrackerPage(page, limit, false);
    }
};

/**
 * Create a project on the fly, so a user who is already working can start
 * tracking without leaving for the admin panel first. Mirrors the admin widget's
 * quick-project affordance (`createQuickProject`).
 *
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {string} title Project title.
 * @returns {Promise<object>} The created project document.
 * @throws {Error} When the title is empty or the backend refuses.
 */
export const createTrackerProject = async (scope, title) => {
    const trimmed = String(title || '').trim();
    if (!trimmed) {
        throw new Error('A project title is required.');
    }

    const response = await apiFetch('/projects', {
        method: 'POST',
        headers: scopeHeaders(scope),
        // `inProgress`, not `active`: `active` is NOT in the backend's
        // `projectStatuses` enum, so `createProject` would silently coerce it to
        // `draft` (the admin widget has exactly that bug). A project created
        // because someone is starting a clock on it right now IS in progress.
        body: JSON.stringify({title: trimmed, status: 'inProgress'}),
    });
    const project = normalizeRecord(response);

    if (!project) {
        throw new Error('The project was created but no identifier was returned.');
    }

    return project;
};

/**
 * Create a task on a project — the "it is a new task" half of the tracker.
 *
 * The backend timer takes an ObjectId `taskId` and has no free-text task field,
 * so a brand-new task is a real `Task` document created first and tracked second.
 * `boardId`/`columnKey` are resolved server-side from the project's default board.
 *
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {string} projectId Owning project id.
 * @param {string} title Task title.
 * @returns {Promise<object>} The created task document.
 * @throws {Error} When the input is incomplete or the backend refuses.
 */
export const createTrackerTask = async (scope, projectId, title) => {
    const trimmed = String(title || '').trim();
    if (!projectId) {
        throw new Error('A project is required before a task can be created.');
    }
    if (!trimmed) {
        throw new Error('A task title is required.');
    }

    const response = await apiFetch('/tasks', {
        method: 'POST',
        headers: scopeHeaders(scope),
        body: JSON.stringify({projectId: String(projectId), title: trimmed}),
    });
    const task = normalizeRecord(response);

    if (!task) {
        throw new Error('The task was created but no identifier was returned.');
    }

    return task;
};

/**
 * POST one widget-timer-facade verb and return the server's own timer state.
 *
 * Unlike the widget's `widgetActions` twin this one THROWS, keeping `error.code`
 * (`no_active_timer`, `service_feature_disabled`, `forbidden_scope`, …) so the
 * screen can say what actually went wrong.
 *
 * @param {'start'|'pause'|'resume'|'break'|'stop'} segment Facade sub-path.
 * @param {object} [body] JSON body (`{}` for the target-less verbs).
 * @returns {Promise<object|null>} The post-mutation timer, or null when idle.
 * @throws {Error} The `apiRequest` error, with `status`/`code`/`payload` attached.
 */
const callTimerFacade = async (segment, body = {}) => {
    const response = await apiFetch(`${TIMER_BASE}/${segment}`, {
        method: 'POST',
        body: JSON.stringify(body),
    });

    return response && response.timer ? response.timer : null;
};

/**
 * Start tracking, creating the task (and, if asked, the project) first.
 *
 * This is the whole "start a task that is not in the list" flow in one place, in
 * the same order the admin widget performs it: mint the project, mint the task,
 * then start — and abort BEFORE starting if either mint came back without an id,
 * because a timer booked against an unreferenceable task can never be rolled up.
 *
 * A start always settles whatever the caller already had running; the backend
 * does that atomically, so the screen does not need to stop first.
 *
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {object} target What to track.
 * @param {'project'|'workOrder'} [target.kind] Tracked entity kind (default `project`).
 * @param {string} [target.id] Existing project / work-order id.
 * @param {string} [target.taskId] Existing task id.
 * @param {string} [target.taskTitle] Title of a NEW task to create and track.
 * @param {string} [target.projectTitle] Title of a NEW project to create and track.
 * @param {{lat: number, lng: number, accuracy?: number}} [target.location] Work-event fix; the server records the `work_order` ping from it.
 * @returns {Promise<{timer: (object|null), project: (object|null), task: (object|null)}>}
 *   The server's timer state plus anything that was created along the way.
 * @throws {Error} On invalid input or any backend refusal.
 */
export const startTracking = async (scope, target = {}) => {
    const kind = target.kind === 'workOrder' ? 'workOrder' : 'project';
    let project = null;
    let task = null;
    let id = target.id ? String(target.id) : '';
    let taskId = target.taskId ? String(target.taskId) : '';

    if (kind === 'project' && !id) {
        project = await createTrackerProject(scope, target.projectTitle);
        id = String(project._id || project.id);
    }

    if (!id) {
        throw new Error('Nothing to track: no project or work order was given.');
    }

    if (kind === 'project' && !taskId && String(target.taskTitle || '').trim()) {
        task = await createTrackerTask(scope, id, target.taskTitle);
        taskId = String(task._id || task.id);
    }

    let timer = null;
    try {
        timer = await callTimerFacade('start', {
            kind,
            id,
            ...(taskId ? {taskId} : {}),
            ...(kind === 'workOrder' && target.location ? {location: target.location} : {}),
        });
    } catch (error) {
        // The project/task are already ON THE SERVER. Losing their ids here is
        // what makes a second Start mint a duplicate of both, so they ride out
        // on the error and the caller adopts them before it retries.
        error.created = {project, task};

        throw error;
    }

    return {timer, project, task};
};

/**
 * Pause the caller's running timer — untracked dead time. The backend closes the
 * running row exactly as a stop does and stamps `pausedAt`, so no duration or
 * invoicing math changes; the row simply becomes resumable. Takes no target.
 * @returns {Promise<object|null>} The post-mutation timer.
 * @throws {Error} `no_active_timer` when nothing is running.
 */
export const pauseTracking = async () => callTimerFacade('pause', {});

/**
 * Resume the caller's paused timer — opens a NEW row on the same project + task.
 * Takes no target.
 * @returns {Promise<object|null>} The post-mutation timer.
 * @throws {Error} `no_paused_timer` when nothing is paused.
 */
export const resumeTracking = async () => callTimerFacade('resume', {});

/**
 * Switch a running WORK-ORDER timer to a tracked BREAK segment. Unlike pause, a
 * break IS counted. Projects cannot break — the facade answers 409
 * `break_not_supported`. Takes no target.
 * @returns {Promise<object|null>} The post-mutation timer.
 * @throws {Error} On 409 for a project timer, or when nothing is running.
 */
export const breakTracking = async () => callTimerFacade('break', {});

/**
 * Stop the caller's timer. Works from `running`, `break` AND `paused` (stopping
 * while paused clears `pausedAt`, otherwise the widget would offer Resume
 * forever). Takes no target.
 * @returns {Promise<object|null>} Always null — nothing is running afterwards.
 * @throws {Error} When there is neither a running nor a paused timer.
 */
export const stopTracking = async () => callTimerFacade('stop', {});

/**
 * Come back to work from a tracked BREAK.
 *
 * The facade's `resume` verb only knows how to reopen a PAUSED row; while a BREAK
 * segment is active it returns the unchanged break state. Returning to work is
 * therefore a re-`start` of the same target, which closes the BREAK segment and
 * opens a WORK one — and `taskId` MUST ride along, because the backend closes the
 * active segment by (technician, work order, task).
 *
 * @param {object} timer The current timer state (contract C1).
 * @returns {Promise<object|null>} The post-mutation timer.
 * @throws {Error} When the timer carries no target.
 */
export const resumeWorkFromBreak = async (timer) => {
    if (!timer?.id) {
        throw new Error('There is no timer to resume.');
    }

    return callTimerFacade('start', {
        kind: timer.kind === 'project' ? 'project' : 'workOrder',
        id: String(timer.id),
        ...(timer.taskId ? {taskId: String(timer.taskId)} : {}),
    });
};

/**
 * The caller's most recent finished time entries, newest first — the screen's
 * "what did I do today" list. Project entries only; work-order segments live on
 * their own work order.
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {{limit?: number, from?: string, to?: string, userId?: string}} [options] Query options.
 * @returns {Promise<object[]>} Fail-soft — `[]` on any error.
 */
export const fetchRecentTimeEntries = async (scope, options = {}) => {
    try {
        const query = buildQuery({
            limit: clampLimit(options.limit),
            from: options.from,
            to: options.to,
            userId: options.userId,
        });
        const response = await apiFetch(`/projects/time-entries?${query}`, {headers: scopeHeaders(scope)});

        // `listAllTimeEntries` applies no `isRunning`/`end` filter, so the clock
        // ticking in the hero above would otherwise also head this list as a
        // second, "0p" row.
        return normalizeList(response, ['entries', 'timeEntries'])
            .filter((entry) => entry && !entry.isRunning && entry.end);
    } catch (error) {
        logTimeTrackingError('fetchRecentTimeEntries', error);

        return [];
    }
};

/**
 * Day / week / month roll-ups for the caller.
 *
 * The period boundaries are computed HERE, in the device's own timezone, and sent
 * along: the server buckets by whatever `dayStart`/`weekStart`/`monthStart` it is
 * given, so a phone in CET and a server in UTC would otherwise disagree about
 * when "today" started. Running timers are excluded server-side, so a live clock
 * can never inflate a total.
 *
 * @param {{restaurantId?: string}} scope Active management scope.
 * @param {{userId?: string, now?: Date}} [options] Query options.
 * @returns {Promise<{day: (object|null), week: (object|null), month: (object|null)}>} Fail-soft.
 */
export const fetchTimeSummary = async (scope, options = {}) => {
    const empty = {day: null, week: null, month: null};

    try {
        const now = options.now instanceof Date ? options.now : new Date();

        const dayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        // ISO weeks start on Monday; `getDay()` returns 0 for Sunday.
        const weekStart = new Date(dayStart);
        weekStart.setDate(dayStart.getDate() - ((dayStart.getDay() + 6) % 7));
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

        const query = buildQuery({
            dayStart: dayStart.toISOString(),
            weekStart: weekStart.toISOString(),
            monthStart: monthStart.toISOString(),
            userId: options.userId,
        });
        const response = await apiFetch(`/projects/time-entries/summary?${query}`, {headers: scopeHeaders(scope)});
        const data = response?.data && typeof response.data === 'object' ? response.data : response;

        if (!data || typeof data !== 'object') {
            return empty;
        }

        return {
            day: data.day || null,
            week: data.week || null,
            month: data.month || null,
        };
    } catch (error) {
        logTimeTrackingError('fetchTimeSummary', error);

        return empty;
    }
};

export default {
    TIME_TRACKING_PAGE_SIZE,
    emptyTrackerPage,
    fetchTrackerState,
    fetchTrackerProjects,
    fetchTrackerTasks,
    createTrackerProject,
    createTrackerTask,
    startTracking,
    pauseTracking,
    resumeTracking,
    breakTracking,
    stopTracking,
    resumeWorkFromBreak,
    fetchRecentTimeEntries,
    fetchTimeSummary,
};
